/**
 * Centralizirani dashboard palette tokens i helperi.
 *
 * Tailwind JIT ne radi s dinamičkim klasama (`bg-${token}`), pa ovaj file
 * vraća **pune Tailwind klase** kao stringove. Kartice koriste pomoćne
 * funkcije umjesto sastavljanja klasa preko template literala.
 *
 * Sky / Indigo paleta (kao primarni AutoBiz akcent — prati postojeći
 * Tabs `border-sky-600` underline u app-u).
 */

export type DeltaDirection = "positive" | "negative" | "neutral";

/** Multi-series boje za bar / area / chart segmente — cyclički indeks. */
const SERIES_BG = [
  "bg-sky-500",
  "bg-indigo-500",
  "bg-teal-500",
  "bg-amber-500",
  "bg-rose-500",
];
const SERIES_BG_LIGHT = [
  "bg-sky-200",
  "bg-indigo-200",
  "bg-teal-200",
  "bg-amber-200",
  "bg-rose-200",
];
const SERIES_HOVER = [
  "hover:bg-sky-600",
  "hover:bg-indigo-600",
  "hover:bg-teal-600",
  "hover:bg-amber-600",
  "hover:bg-rose-600",
];

/** Raw hex za SVG `stroke` / `fill` (gdje Tailwind klase ne idu). */
const SERIES_HEX = [
  "#0ea5e9", // sky-500
  "#6366f1", // indigo-500
  "#14b8a6", // teal-500
  "#f59e0b", // amber-500
  "#f43f5e", // rose-500
];

export const seriesBg = (i: number) => SERIES_BG[i % SERIES_BG.length];
export const seriesBgLight = (i: number) => SERIES_BG_LIGHT[i % SERIES_BG_LIGHT.length];
export const seriesHover = (i: number) => SERIES_HOVER[i % SERIES_HOVER.length];
export const seriesHex = (i: number) => SERIES_HEX[i % SERIES_HEX.length];

/** Primarni akcent (linija na main chart-u, sparkline, KPI accent). */
export const primary = {
  bg: "bg-sky-500",
  bgSoft: "bg-sky-100",
  bgWash: "bg-sky-50",
  text: "text-sky-600",
  textStrong: "text-sky-700",
  hex: "#0ea5e9",
  hexSoft: "rgba(14, 165, 233, 0.15)",
};

/** Sekundarni akcent (drugi line, kategorija usporedbe). */
export const secondary = {
  bg: "bg-indigo-500",
  bgSoft: "bg-indigo-100",
  bgWash: "bg-indigo-50",
  text: "text-indigo-600",
  hex: "#6366f1",
};

/** Semantičke boje (positive / negative / warning / neutral). */
export const positive = {
  text: "text-emerald-700",
  bg: "bg-emerald-50",
  solid: "bg-emerald-500",
  hex: "#10b981",
  hexSoft: "rgba(16, 185, 129, 0.15)",
};
export const negative = {
  text: "text-rose-600",
  bg: "bg-rose-50",
  solid: "bg-rose-500",
  hex: "#f43f5e",
  hexSoft: "rgba(244, 63, 94, 0.15)",
};
export const warning = {
  text: "text-amber-700",
  bg: "bg-amber-50",
  solid: "bg-amber-500",
};
export const neutral = {
  text: "text-slate-700",
  bg: "bg-slate-100",
  solid: "bg-slate-300",
};

/** Tipografija + strukturalni tokeni. */
export const text = {
  primary: "text-slate-900",
  secondary: "text-slate-600",
  muted: "text-slate-500",
  subtle: "text-slate-400",
};
export const border = {
  default: "border-slate-200",
  strong: "border-slate-300",
};

/**
 * Kompaktne varijante prema širini kartice, ne prozora.
 *
 * `data-size` postavljaju `GridWidget` i `OverviewCard` na omotač koji nosi
 * `group/dashcard`, pa se ovdje čita kroz `group-data-[...]`. Klase su ispisane
 * u cijelosti jer Tailwind JIT ne vidi sastavljene stringove.
 */
export const compact = {
  /** Unutarnji razmak kartice. */
  padding: "px-4 py-5 group-data-[size=sm]/dashcard:px-2 group-data-[size=sm]/dashcard:py-3",
  /** Naslov kartice. */
  title: "text-md group-data-[size=sm]/dashcard:text-sm",
  /** Opis ispod naslova — na uskoj kartici se gubi. */
  subtitle: "text-sm group-data-[size=sm]/dashcard:hidden",
  /** Veliki KPI broj (ukupno vozila, iznos prihoda…). */
  metric:
    "text-2xl group-data-[size=md]/dashcard:text-xl group-data-[size=sm]/dashcard:text-base",
  /** Legenda i oznake osi. */
  legend: "text-sm group-data-[size=sm]/dashcard:text-xs",
};

/**
 * Vrati boje za delta badge na temelju smjera.
 *
 * Ako je `inverted = true`, pozitivan delta se boja kao "loš" (npr. rast troškova).
 */
export const getDeltaColors = (
  value: number,
  inverted: boolean = false
): { text: string; bg: string; arrow: "↑" | "↓" | "—" } => {
  if (value === 0) {
    return { text: "text-slate-500", bg: "bg-slate-100", arrow: "—" };
  }
  const isUp = value > 0;
  const isPositive = inverted ? !isUp : isUp;
  return {
    text: isPositive ? positive.text : negative.text,
    bg: isPositive ? positive.bg : negative.bg,
    arrow: isUp ? "↑" : "↓",
  };
};
